import { useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useUploadStore } from "../../store/uploadStore";
import { apiService } from "../../services/apiService";
import type { User } from "../../types";

/**
 * Platform 파일 체크 로직을 처리하는 훅
 */
export const usePlatformFileCheck = () => {
  const { user } = useAuth();
  const completeGlobal = useUploadStore((s) => s.complete);
  const userToken = (user as User)?.token;

  const checkPlatformFileFromMainAPI = useCallback(
    async (
      id: string,
      uid: string,
      modelName: string,
      originalFilename: string
    ): Promise<{ signedUrl: string; gcsPath: string; fileName: string } | null> => {
      try {
        const token = userToken;
        if (!token || !uid || !modelName) {
          return null;
        }

        // Check integrated platform-specific files from Main API server
        const platformResult = (await apiService.getPlatformFile(
          token,
          uid,
          modelName
        )) as
          | {
              success?: boolean;
              gcs_path?: string;
              signed_url?: string;
              file_name?: string;
            }
          | null;

        if (!platformResult || !platformResult.success) {
          return null;
        }

        const { gcs_path, signed_url, file_name } = platformResult;

        const latestItem = useUploadStore.getState().items[id];
        if (latestItem?.cancelled) {
          return null;
        }

        // Complete processing
        completeGlobal({ id, fileUrl: signed_url, gcsPath: gcs_path });

        // Trigger global event: work completed
        try {
          const evt = new CustomEvent("upload:workCompleted", {
            detail: { id, status: "completed" },
          });
          window.dispatchEvent(evt);

          // Auto-select newly uploaded VSE in Unity for empty spaces
          setTimeout(() => {
            try {
              console.log(
                "[usePlatformFileCheck] 🎯 새로 업로드된 VSE 자동 선택 시도:",
                { id, fileName: originalFilename }
              );
              window.dispatchEvent(
                new CustomEvent("upload:selectNewVSE", {
                  detail: { id, fileName: originalFilename },
                })
              );
            } catch (e) {
              console.warn(
                "[usePlatformFileCheck] ⚠️ VSE 자동 선택 이벤트 발송 실패:",
                e
              );
            }
          }, 1000);
        } catch (_) {
          // Ignore errors
        }

        return {
          signedUrl: signed_url || "",
          gcsPath: gcs_path || "",
          fileName: file_name || "",
        };
      } catch (error) {
        console.error(
          "[usePlatformFileCheck] Error checking platform file via Main API:",
          error
        );
        return null;
      }
    },
    [userToken, completeGlobal]
  );

  return { checkPlatformFileFromMainAPI };
};
